import { HealthcareFacility } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { FormContextValues, useFormController } from '@dreryk/react-common';
import { AutoLayout } from '@dreryk/react-components';

import { healthcareOrganizationTranslationKeys } from '../../../../translations';
import {
   createHealthcareAddressFormBean,
   HealthcareAddressFormBean,
   HealthcareAddressFormPart,
} from '../../../common/HealthcareAddressFormPart';
import { HealthcareBasicData } from '../../../common/HealthcareBasicData';
import {
   createHealthcareContactFormBean,
   HealthcareContactFormBean,
   HealthcareContactFormPart,
} from '../../../common/HealthcareContactFormPart';
import { HealthcareIdFormController } from '../../../common/HealthcareIdFormController';
import { HealthcareProviderNameInput } from '../../../common/HealthcareProviderNameInput';
import { RegonInput } from '../../../common/RegonInput';

export type HealthcareFacilityUpdateFormBean = {
   id: number;
   name: string;
   regon: string | null;
} & HealthcareAddressFormBean &
   HealthcareContactFormBean;

export type HealthcareFacilityUpdateFormController = FormContextValues<HealthcareFacilityUpdateFormBean>;

const getInitialValues = (healthcareFacility: HealthcareFacility): HealthcareFacilityUpdateFormBean => {
   return {
      id: healthcareFacility.id,
      name: healthcareFacility.name,
      regon: healthcareFacility.regon?.asString() ?? null,
      ...createHealthcareAddressFormBean(
         healthcareFacility.usingHealthcareOrganizationAddressData,
         healthcareFacility.address
      ),
      ...createHealthcareContactFormBean(healthcareFacility.usingHealthcareOrganizationContactData, healthcareFacility),
   };
};

type HealthcareFacilityUpdateFormRenderProps = Pick<
   HealthcareFacilityUpdateFormController,
   'handleSubmit' | 'formState'
>;

type HealthcareFacilityUpdateFormProps = {
   readonly healthcareFacility: HealthcareFacility;
   readonly healthcareParentData: HealthcareBasicData;
   readonly children: (renderProps: HealthcareFacilityUpdateFormRenderProps) => JSX.Element;
};

export const HealthcareFacilityUpdateForm = (props: HealthcareFacilityUpdateFormProps): JSX.Element => {
   const { register, handleSubmit, formState, watch } = useFormController<HealthcareFacilityUpdateFormBean>(
      getInitialValues(props.healthcareFacility)
   );

   const { usingParentAddressData, usingParentContactData } = watch([
      'usingParentAddressData',
      'usingParentContactData',
   ]);

   return (
      <AutoLayout as={'form'} noValidate $direction={'vertical'} $horizontalResizing={'fill'} $spacing={36}>
         <HealthcareIdFormController name={'id'} register={register} required />
         <AutoLayout
            as={HealthcareProviderNameInput}
            name={'name'}
            register={register}
            required
            $horizontalResizing={'fill'}
         />

         <HealthcareAddressFormPart
            register={register}
            usingParentAddressData={usingParentAddressData}
            usingParentAddressDataLabel={healthcareOrganizationTranslationKeys.usingHealthcareOrganizationAddressData}
            healthcareParentData={props.healthcareParentData}
         />

         <HealthcareContactFormPart
            register={register}
            usingParentContactData={usingParentContactData}
            usingParentContactDataLabel={healthcareOrganizationTranslationKeys.usingHealthcareOrganizationContactData}
            healthcareParentData={props.healthcareParentData}
         />

         <AutoLayout as={'div'} $direction={'horizontal'} $horizontalResizing={357}>
            <RegonInput name={'regon'} register={register} type={'unit'} required />
         </AutoLayout>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </AutoLayout>
   );
};
